// Pulls a per-tenant slug out of a Fortius hostname so the frontend can resolve
// `clientId` from the URL before Auth0 loads. Mirrors the slug format enforced
// by the DB CHECK constraint in V15__add_client_subdomain_slug.sql and the API
// at ClientService.IsValidSubdomainSlug.
//
// Examples:
//   southbank.fortius-app.com            -> "southbank"
//   southbank.lon.fortius-app.com        -> "southbank"     (prod lon stack)
//   charingcross.dev.lon.fortius-app.com -> "charingcross"  (dev lon stack)
//   dev.fortius-app.com                  -> null            (reserved stack subdomain)
//   www.fortius-app.com                  -> null
//   fortius-app.com                      -> null
//   southbank.localhost                  -> "southbank"     (local dev subdomain)
//   localhost / 127.0.0.1                -> null            (apex / no subdomain)

const FORTIUS_APEX = 'fortius-app.com';

// Local-dev apex: `<slug>.localhost` mirrors `<slug>.fortius-app.com` so the
// subdomain tenant flow can be exercised against the local stack. Chrome
// resolves `*.localhost` to loopback automatically; other browsers need an
// `/etc/hosts` entry (e.g. `127.0.0.1 southbank.localhost`). Bare `localhost`
// stays apex (returns null) — only a real left-most label is a tenant slug.
const LOCAL_APEX = 'localhost';

const RESERVED_LABELS = new Set([
    'www',
    'dev',
    'api',
    'api-dev',
    'lon',
    'london',
    'auth',
]);

// Environment/region labels that may sit between a tenant slug and the apex,
// e.g. `<slug>.lon`, `<slug>.dev.lon`. The slug is always the left-most label;
// every label after it must be one of these stack segments.
const STACK_LABELS = new Set(['dev', 'lon']);

// Canonical slug format, shared with every other slug-aware site:
//   - DB CHECK constraint in V15__add_client_subdomain_slug.sql
//   - Backend ClientService.IsValidSubdomainSlug
//   - Frontend triggerSignup (signup CTA guard)
//   - Auth0 add-fortius-user action (normalisation before stamping)
// Keep them in sync; the backend will reject anything this pattern lets through.
export const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export function extractSubdomainSlug(hostname: string): string | null {
    if (!hostname) return null;

    const lower = hostname.toLowerCase();

    // Resolve which apex this host hangs off — the prod domain or the local-dev
    // `localhost` apex. Anything else (a bare IP, an unrelated domain) is not a
    // Fortius host and has no tenant slug.
    const apex =
        lower === FORTIUS_APEX || lower.endsWith(`.${FORTIUS_APEX}`)
            ? FORTIUS_APEX
            : lower === LOCAL_APEX || lower.endsWith(`.${LOCAL_APEX}`)
                ? LOCAL_APEX
                : null;
    if (apex === null) return null;

    if (lower === apex) return null;

    const prefix = lower.slice(0, -(apex.length + 1));
    const labels = prefix.split('.');

    // Accept `<slug>` optionally followed by stack/region labels, e.g.
    // `<slug>.fortius-app.com`, `<slug>.lon.fortius-app.com`,
    // `<slug>.dev.lon.fortius-app.com`. Anything else (multiple non-reserved
    // labels, unknown middle label) is treated as not a tenant slug.
    const candidate = labels[0];
    const trailing = labels.slice(1);
    if (!trailing.every((label) => STACK_LABELS.has(label))) return null;

    if (RESERVED_LABELS.has(candidate)) return null;
    if (!SLUG_PATTERN.test(candidate)) return null;

    return candidate;
}

// Inverse of extractSubdomainSlug: given a tenant slug, build the absolute URL
// of that tenant's site by prepending the slug to the *current* apex host. This
// keeps the apex /clubs redirect environment-agnostic — no hardcoded per-stack
// host map — because the apex host already encodes the stack. It carries over
// the current protocol and `host` (which includes the port), so the target is
// well-formed in local dev too, not just on the prod apex:
//
//   buildTenantUrl('southbank', 'fortius-app.com', 'https:')        -> https://southbank.fortius-app.com
//   buildTenantUrl('southbank', 'www.fortius-app.com', 'https:')    -> https://southbank.fortius-app.com
//   buildTenantUrl('southbank', 'dev.lon.fortius-app.com', 'https:')-> https://southbank.dev.lon.fortius-app.com
//   buildTenantUrl('southbank', 'localhost:3000', 'http:')          -> http://southbank.localhost:3000
//
// Returns null for slugs that fail SLUG_PATTERN — defence-in-depth, since the
// slug originates from an API payload a tenant admin can influence and is about
// to be interpolated into a redirect target.
export function buildTenantUrl(
    slug: string,
    host: string = window.location.host,
    protocol: string = window.location.protocol,
): string | null {
    if (!slug || !SLUG_PATTERN.test(slug)) return null;

    const lowerHost = host.toLowerCase();
    // Strip a leading `www.` so we don't produce `<slug>.www.fortius-app.com`.
    const apexHost = lowerHost.startsWith('www.') ? lowerHost.slice(4) : lowerHost;
    if (!apexHost) return null;

    return `${protocol}//${slug}.${apexHost}`;
}
